import React, { useState, useRef, useEffect } from "react";
import { DropdownIcon } from "../../../fixedassetelements/button/Button";
import styles from "./CustomDropdown.module.css";

const CustomDropdown = ({
  label,
  required = false,
  value = "",
  onChange,
  options = [],
  placeholder = "Select Option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef(null);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const container = listRef.current;
      const item = container.children[highlightedIndex];
      if (item) {
        const itemTop = item.offsetTop;
        const itemBottom = itemTop + item.offsetHeight;
        const containerScrollTop = container.scrollTop;
        const containerHeight = container.clientHeight;

        if (itemTop < containerScrollTop) {
          container.scrollTop = itemTop;
        } else if (itemBottom > containerScrollTop + containerHeight) {
          container.scrollTop = itemBottom - containerHeight;
        }
      }
    }
  }, [highlightedIndex]);

  const toggleDropdown = () => setIsOpen((prev) => !prev);
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") setIsOpen(true);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => 
        prev < filteredOptions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleSelect(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);


  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    const filtered = options.filter((opt) =>
      opt.toLowerCase().includes(inputValue.toLowerCase())
    );

    setFilteredOptions(filtered);
    setIsOpen(true);
  };

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={styles.fieldGroup} ref={dropdownRef}>
      {label && (
        <label className={styles.label}>
          {required && <span className={styles.required}>* </span>}
          {label}
        </label>
      )}

      <div className={styles.dropdownContainer}>
        <div className={styles.inputWrapper} onClick={() => setIsOpen(true)}>
          <input
            type="text"
            className={styles.input}
            placeholder={placeholder}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
          />
        </div>

        <div className={styles.iconWrapper} onClick={toggleDropdown} style={{ cursor: "pointer" }}>
          <DropdownIcon />
        </div>

        {isOpen && (
          <div className={styles.dropdownMenu} ref={listRef}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`${styles.dropdownItem} ${highlightedIndex === index ? styles.dropdownItemHighlighted : ""}`}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option}
                </div>
              ))
            ) : (
              <div className={styles.dropdownItem}>
                No results found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDropdown;
